import { Platform } from 'react-native';
import {
  GoogleAuthProvider,
  OAuthProvider,
  signInWithCredential,
  signInWithPopup,
  signInWithRedirect,
  getRedirectResult,
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  sendPasswordResetEmail,
  signOut as firebaseSignOut,
  onAuthStateChanged,
  updateProfile,
  reauthenticateWithPopup,
  revokeAccessToken,
} from '@firebase/auth';
import { callDeleteMyAccount } from './functions';
import { auth } from './config';
import { savePushToken } from './users';

// Native Apple Sign-In (iOS only). Loaded lazily so web/Android don't require it.
let AppleAuthentication = null;
let Crypto = null;
try {
  AppleAuthentication = require('expo-apple-authentication');
  Crypto = require('expo-crypto');
} catch {
  // Not available on this platform
}

// Mobile-only Google Sign-In
let GoogleSignin = null;
export let statusCodes = {};
try {
  const pkg = require('@react-native-google-signin/google-signin');
  GoogleSignin = pkg.GoogleSignin;
  statusCodes = pkg.statusCodes;
  GoogleSignin.configure({
    webClientId: '711734326364-cvgq693mge2m8se9qbrbvgdr8dolool8.apps.googleusercontent.com',
    offlineAccess: false,
  });
} catch {
  // Web — not available
}

// Completes a pending web redirect sign-in. Memoized so App (which starts it)
// and LoginScreen (which reports a failure) share the one result. Resolves to
// { error } instead of rejecting — a failure used to be swallowed entirely,
// dropping the user back on the login page with no idea why.
let redirectResult = null;
export const handleWebRedirectResult = () => {
  if (!redirectResult) {
    redirectResult = getRedirectResult(auth)
      .then((result) => ({ result, error: null }))
      .catch((error) => ({ result: null, error }));
  }
  return redirectResult;
};

export const signInWithGoogle = async () => {
  if (Platform.OS === 'web') {
    const provider = new GoogleAuthProvider();
    try {
      return await signInWithPopup(auth, provider);
    } catch (e) {
      // Popup blocked / closed / unsupported (Safari, in-app browsers) → fall back to redirect.
      const fallbackCodes = [
        'auth/popup-blocked',
        'auth/popup-closed-by-user',
        'auth/cancelled-popup-request',
        'auth/operation-not-supported-in-this-environment',
        'auth/web-storage-unsupported',
      ];
      if (fallbackCodes.includes(e?.code)) {
        await signInWithRedirect(auth, provider);
        return null; // result is handled on reload by handleWebRedirectResult()
      }
      throw e;
    }
  }
  await GoogleSignin.hasPlayServices({ showPlayServicesUpdateDialog: true });
  const userInfo = await GoogleSignin.signIn();
  // v13+ resolves with {type: 'cancelled'} instead of throwing when the user
  // dismisses the sheet — treat it as a no-op, not an error.
  if (userInfo?.type === 'cancelled') return null;
  const idToken = userInfo.data?.idToken ?? userInfo.idToken;
  if (!idToken) throw new Error('No ID token returned from Google Sign-In');
  const credential = GoogleAuthProvider.credential(idToken);
  return signInWithCredential(auth, credential);
};

// Whether Sign in with Apple can be offered on this device (iOS 13+).
export const isAppleAuthAvailable = async () => {
  if (Platform.OS !== 'ios' || !AppleAuthentication) return false;
  try {
    return await AppleAuthentication.isAvailableAsync();
  } catch {
    return false;
  }
};

export const signInWithApple = async () => {
  if (!AppleAuthentication || !Crypto) throw new Error('Apple Sign-In is not available.');
  // Generate a nonce; send the SHA-256 hash to Apple and the raw value to Firebase.
  const bytes = await Crypto.getRandomBytesAsync(16);
  const rawNonce = Array.from(bytes).map((b) => b.toString(16).padStart(2, '0')).join('');
  const hashedNonce = await Crypto.digestStringAsync(Crypto.CryptoDigestAlgorithm.SHA256, rawNonce);

  const appleCredential = await AppleAuthentication.signInAsync({
    requestedScopes: [
      AppleAuthentication.AppleAuthenticationScope.FULL_NAME,
      AppleAuthentication.AppleAuthenticationScope.EMAIL,
    ],
    nonce: hashedNonce,
  });

  if (!appleCredential.identityToken) {
    throw new Error('No identity token returned from Apple.');
  }

  const provider = new OAuthProvider('apple.com');
  const firebaseCredential = provider.credential({
    idToken: appleCredential.identityToken,
    rawNonce,
  });
  const result = await signInWithCredential(auth, firebaseCredential);

  // Apple only returns the name on the very first sign-in — capture it once.
  const fn = appleCredential.fullName;
  if (fn && (fn.givenName || fn.familyName) && result.user && !result.user.displayName) {
    const name = [fn.givenName, fn.familyName].filter(Boolean).join(' ').trim();
    if (name) {
      try { await updateProfile(result.user, { displayName: name }); } catch {}
    }
  }
  return result;
};

// Web Sign in with Apple (popup, with redirect fallback for blocked popups).
export const signInWithAppleWeb = async () => {
  const provider = new OAuthProvider('apple.com');
  provider.addScope('email');
  provider.addScope('name');
  try {
    return await signInWithPopup(auth, provider);
  } catch (e) {
    const fallbackCodes = [
      'auth/popup-blocked',
      'auth/popup-closed-by-user',
      'auth/cancelled-popup-request',
      'auth/operation-not-supported-in-this-environment',
      'auth/web-storage-unsupported',
    ];
    if (fallbackCodes.includes(e?.code)) {
      await signInWithRedirect(auth, provider);
      return null; // handled on reload by handleWebRedirectResult()
    }
    throw e;
  }
};

export const signUpWithEmail = (email, password) =>
  createUserWithEmailAndPassword(auth, email, password);

export const signInWithEmail = (email, password) =>
  signInWithEmailAndPassword(auth, email, password);

export const resetPassword = (email) =>
  sendPasswordResetEmail(auth, email);

export const signOut = async () => {
  // Detach this device's push token from the account being signed out, so it
  // stops receiving that account's notifications (e.g. admin new-signup alerts)
  // once logged out or switched to another account. The token re-registers on
  // next login.
  try { if (auth.currentUser) await savePushToken(auth.currentUser.uid, null); } catch {}
  if (Platform.OS !== 'web' && GoogleSignin) {
    try { await GoogleSignin.signOut(); } catch {}
  }
  await firebaseSignOut(auth);
};

// Apple requires apps with Sign in with Apple to revoke the user's Apple token
// when they delete their account. That needs a fresh Apple authorization, so
// the user is asked to confirm with Apple once more. Throws
// { code: 'apple-cancelled' } if they back out — deletion then stops.
const revokeAppleToken = async (user) => {
  try {
    if (Platform.OS === 'ios' && AppleAuthentication) {
      const cred = await AppleAuthentication.signInAsync({ requestedScopes: [] });
      if (cred?.authorizationCode) await revokeAccessToken(auth, cred.authorizationCode);
    } else if (Platform.OS === 'web') {
      const result = await reauthenticateWithPopup(user, new OAuthProvider('apple.com'));
      const token = OAuthProvider.credentialFromResult(result)?.accessToken;
      if (token) await revokeAccessToken(auth, token);
    }
    // Android has no Apple sign-in to revoke from; the account is still deleted.
  } catch (e) {
    const cancelled = ['ERR_REQUEST_CANCELED', 'ERR_CANCELED', 'auth/popup-closed-by-user', 'auth/cancelled-popup-request', 'auth/user-cancelled'];
    if (cancelled.includes(e?.code)) {
      const err = new Error('Apple confirmation was cancelled.');
      err.code = 'apple-cancelled';
      throw err;
    }
    // Any other failure (network, popup blocked) must not trap the user in an
    // account they asked to delete — the data deletion below still runs.
    console.warn('Apple token revocation failed:', e?.code, e?.message);
  }
};

// Deletes the account and its data server-side (no recent sign-in needed), then
// clears this device. Returns the server's counts of what was removed.
export const deleteAccount = async () => {
  const user = auth.currentUser;
  if (!user) throw new Error('No user signed in');
  if ((user.providerData || []).some((p) => p.providerId === 'apple.com')) {
    await revokeAppleToken(user);
  }
  const counts = await callDeleteMyAccount();
  if (Platform.OS !== 'web' && GoogleSignin) {
    try { await GoogleSignin.signOut(); } catch {}
  }
  try { await firebaseSignOut(auth); } catch {}
  return counts;
};

export const subscribeToAuthChanges = (callback) =>
  onAuthStateChanged(auth, callback);
